import React, { useState, useEffect } from "react";
import { usePrivy } from "@privy-io/react-auth";
import { useNavigate, useLocation } from "react-router-dom";
import { CountryCodeAlpha2, USStateCodeAlpha2 } from "../utils/constants";
import i18n from "i18n-iso-countries";
import en from "i18n-iso-countries/langs/en.json";
import { BusinessInfo, ValidationErrors } from "../types/businessTypes";
import {
  getPersonaTemplateId,
  getBusinessGroup,
  getBusinessGroupName,
} from "../utils/PersonaClassifier";

import API from "../api/api";

i18n.registerLocale(en);

const MONTHLY_VOLUME_OPTIONS = [
  "None",
  "<$100K",
  "$100K-$500K",
  "$500k-$1M",
  "$1M-$10M",
  "$10M-$50M",
  ">$50M",
];

const TRANSACTIONS_PER_MONTH_OPTIONS = [
  "None",
  "<100",
  "100-1K",
  "1K-10K",
  "10k+",
];

const PAYMENT_RAILS_OPTIONS = ["ACH", "Wire", "Push-to-Card", "RTP", "FedNow"];

const FIAT_CURRENCIES_OPTIONS = ["USD", "EUR", "Other"];

const DIGITAL_CURRENCIES_OPTIONS = [
  "USDC",
  "BTC",
  "ETH",
  "XRP",
  "SOL",
  "Other",
];

const USE_CASE_OPTIONS = [
  "Consumer on/off-ramps",
  "Business on/off-ramps",
  "Crypto-only payment workflows",
  "Fiat-only payments",
  "U.S. market entry",
];

const PLATFORM_PREFERENCE_OPTIONS = ["No-code dashboard", "API"];

const usStateNames: { [key in USStateCodeAlpha2]: string } = {
  [USStateCodeAlpha2.AL]: "Alabama",
  [USStateCodeAlpha2.AK]: "Alaska",
  [USStateCodeAlpha2.AZ]: "Arizona",
  [USStateCodeAlpha2.AR]: "Arkansas",
  [USStateCodeAlpha2.CA]: "California",
  [USStateCodeAlpha2.CO]: "Colorado",
  [USStateCodeAlpha2.CT]: "Connecticut",
  [USStateCodeAlpha2.DE]: "Delaware",
  [USStateCodeAlpha2.FL]: "Florida",
  [USStateCodeAlpha2.GA]: "Georgia",
  [USStateCodeAlpha2.HI]: "Hawaii",
  [USStateCodeAlpha2.ID]: "Idaho",
  [USStateCodeAlpha2.IL]: "Illinois",
  [USStateCodeAlpha2.IN]: "Indiana",
  [USStateCodeAlpha2.IA]: "Iowa",
  [USStateCodeAlpha2.KS]: "Kansas",
  [USStateCodeAlpha2.KY]: "Kentucky",
  [USStateCodeAlpha2.LA]: "Louisiana",
  [USStateCodeAlpha2.ME]: "Maine",
  [USStateCodeAlpha2.MD]: "Maryland",
  [USStateCodeAlpha2.MA]: "Massachusetts",
  [USStateCodeAlpha2.MI]: "Michigan",
  [USStateCodeAlpha2.MN]: "Minnesota",
  [USStateCodeAlpha2.MS]: "Mississippi",
  [USStateCodeAlpha2.MO]: "Missouri",
  [USStateCodeAlpha2.MT]: "Montana",
  [USStateCodeAlpha2.NE]: "Nebraska",
  [USStateCodeAlpha2.NV]: "Nevada",
  [USStateCodeAlpha2.NH]: "New Hampshire",
  [USStateCodeAlpha2.NJ]: "New Jersey",
  [USStateCodeAlpha2.NM]: "New Mexico",
  [USStateCodeAlpha2.NY]: "New York",
  [USStateCodeAlpha2.NC]: "North Carolina",
  [USStateCodeAlpha2.ND]: "North Dakota",
  [USStateCodeAlpha2.OH]: "Ohio",
  [USStateCodeAlpha2.OK]: "Oklahoma",
  [USStateCodeAlpha2.OR]: "Oregon",
  [USStateCodeAlpha2.PA]: "Pennsylvania",
  [USStateCodeAlpha2.RI]: "Rhode Island",
  [USStateCodeAlpha2.SC]: "South Carolina",
  [USStateCodeAlpha2.SD]: "South Dakota",
  [USStateCodeAlpha2.TN]: "Tennessee",
  [USStateCodeAlpha2.TX]: "Texas",
  [USStateCodeAlpha2.UT]: "Utah",
  [USStateCodeAlpha2.VT]: "Vermont",
  [USStateCodeAlpha2.VA]: "Virginia",
  [USStateCodeAlpha2.WA]: "Washington",
  [USStateCodeAlpha2.WV]: "West Virginia",
  [USStateCodeAlpha2.WI]: "Wisconsin",
  [USStateCodeAlpha2.WY]: "Wyoming",
  [USStateCodeAlpha2.DC]: "District of Columbia",
  [USStateCodeAlpha2.AS]: "American Samoa",
  [USStateCodeAlpha2.GU]: "Guam",
  [USStateCodeAlpha2.MP]: "Northern Mariana Islands",
  [USStateCodeAlpha2.PR]: "Puerto Rico",
  [USStateCodeAlpha2.VI]: "U.S. Virgin Islands",
  [USStateCodeAlpha2.UM]: "U.S. Minor Outlying Islands",
};

const countryOptions = (Object.values(CountryCodeAlpha2) as Array<string>).map(
  (code) => ({
    value: code,
    label: i18n.getName(code, "en") || code,
  }),
);

const usStateOptions = (Object.values(USStateCodeAlpha2) as Array<string>).map(
  (code) => ({
    value: code,
    label: usStateNames[code as USStateCodeAlpha2] || code,
  }),
);

const INDUSTRY_OPTIONS = [
  "Accommodation and Food Services",
  "Administrative and Support Services",
  "Adult Entertainment",
  "Agriculture, Forestry, Fishing and Hunting",
  "Arts, Entertainment, and Recreation",
  "Construction",
  "Educational Services",
  "Finance and Insurance",
  "Gambling",
  "Government",
  "Healthcare and Social Assistance",
  "Information Technology",
  "Management of Companies and Enterprises",
  "Manufacturing",
  "Marijuana or Related Services",
  "Mining, Quarrying, and Oil and Gas Extraction",
  "Non-profit Organization",
  "Professional, Scientific, and Technical Services",
  "Real Estate and Rental and Leasing",
  "Retail Trade",
  "Transportation and Warehousing",
  "Utilities",
  "Weapons, Firearms, and Explosives",
  "Wholesale Trade",
  "Other Services",
];

const CUSTOMER_TYPE_OPTIONS = [
  "Individuals that will store money with my business",
  "Businesses that will store money with my business",
  "Individuals that will transfer money to each other",
  "Businesses that will pay their employees, clients, or end users",
  "Somebody else",
];

const validationRules = {
  name: (value: string) => {
    if (!value) return "Full name is required";
    if (value.length < 2) return "Full name must be at least 2 characters";
    return "";
  },
  companyName: (value: string) => {
    if (!value) return "Company name is required";
    if (value.length < 2) return "Company name must be at least 2 characters";
    return "";
  },
  industry: (value: string) => {
    if (!value) return "Industry is required";
    return "";
  },
  hqCountry: (value: string) => {
    if (!value) return "Headquarters country is required";
    return "";
  },
  hqState: (value: string, allValues?: BusinessInfo) => {
    if (allValues?.hqCountry === CountryCodeAlpha2.US && !value)
      return "State is required for US-based companies";
    return "";
  },
  monthlyVolume: (value: string) => {
    if (!value) return "Monthly volume is required";
    return "";
  },
  transactionsPerMonth: (value: string) => {
    if (!value) return "Transactions per month is required";
    return "";
  },
  paymentRails: (value: string[]) => {
    if (!value.length) return "At least one payment rail is required";
    return "";
  },
  fiatCurrencies: (value: string[]) => {
    if (!value.length) return "At least one fiat currency is required";
    return "";
  },
  digitalCurrencies: (value: string[]) => {
    if (!value.length) return "At least one digital currency is required";
    return "";
  },
  useCase: (value: string[]) => {
    if (!value.length) return "At least one use case is required";
    return "";
  },
  platformPreference: (value: string[]) => {
    if (!value.length) return "At least one platform preference is required";
    return "";
  },
  customerTypes: (value: string[]) => {
    if (!value.length) return "Please select at least one customer type";
    return "";
  },
};

export default function BusinessOnboarding() {
  const { user } = usePrivy();
  const navigate = useNavigate();
  const location = useLocation();
  const startAtStep = location.state?.startAtStep || 0;
  const [currentStep, setCurrentStep] = useState(startAtStep);
  const [leadId, setLeadId] = useState<string | null>(null);
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>({
    name: "",
    companyName: "",
    industry: "",
    hqCountry: null,
    hqState: null,
    monthlyVolume: "",
    transactionsPerMonth: "",
    paymentRails: [],
    fiatCurrencies: [],
    digitalCurrencies: [],
    useCase: [],
    platformPreference: [],
    customerTypes: [],
  });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showHandoffModal, setShowHandoffModal] = useState<boolean>(false);
  const [kybDetails, setKybDetails] = useState({
    businessGroup: 0,
    businessGroupName: "",
  });

  const steps = [
    {
      title: "Business Information",
      fields: ["name", "companyName", "industry", "hqCountry", "hqState"],
    },
    {
      title: "Customer Types",
      fields: ["customerTypes"],
    },
    {
      title: "Transaction Details",
      fields: ["monthlyVolume", "transactionsPerMonth"],
    },
    {
      title: "Payment Rails & Preferences",
      fields: ["paymentRails", "fiatCurrencies", "digitalCurrencies"],
    },
    {
      title: "Use Case & Platform",
      fields: ["useCase", "platformPreference"],
    },
  ];

  useEffect(() => {
    // Fetch existing business lead data or check if user is already associated with a business
    const checkBusinessStatus = async () => {
      if (user?.email?.address) {
        try {
          setIsLoading(true);

          // First check if user is already associated with a business
          const statusResponse = await API.get(
            `/business-lead/status?email=${encodeURIComponent(user.email.address)}`,
          );
          console.log("statusResponse", statusResponse);
          console.log("statusResponse.data", statusResponse.data);

          if (statusResponse.data?.skipOnboarding) {
            if (statusResponse.data?.businessId) {
              navigate(`/accounts/${statusResponse.data.businessId}`);
            } else if (statusResponse.data?.isRejected) {
              navigate("/accounts/rejected");
            } else {
              navigate("/accounts");
            }
            return;
          }

          if (statusResponse.data?.exists) {
            console.log(
              "statusResponse.data?.exists",
              statusResponse.data?.exists,
            );
            // User has started onboarding but not completed it
            // Get the actual business lead data by email
            const { data } = await API.get(
              `/business-lead?email=${encodeURIComponent(user.email.address)}`,
            );

            if (data) {
              setLeadId(data.id);

              // Initialize business info with data from the backend
              setBusinessInfo({
                name: data.name || "",
                companyName: data.companyName || "",
                industry: data.industry || "",
                hqCountry: data.hqCountry || null,
                hqState: data.hqState || null,
                monthlyVolume: data.monthlyVolume || "",
                transactionsPerMonth: data.transactionsPerMonth || "",
                paymentRails: Array.isArray(data.paymentRails)
                  ? data.paymentRails
                  : [],
                fiatCurrencies: Array.isArray(data.fiatCurrencies)
                  ? data.fiatCurrencies
                  : [],
                digitalCurrencies: Array.isArray(data.digitalCurrencies)
                  ? data.digitalCurrencies
                  : [],
                useCase: Array.isArray(data.useCase) ? data.useCase : [],
                platformPreference: Array.isArray(data.platformPreference)
                  ? data.platformPreference
                  : [],
                customerTypes: Array.isArray(data.customerTypes)
                  ? data.customerTypes
                  : [],
              });

              // Set the current step based on the last completed step
              const lastCompletedStep = statusResponse.data.lastCompletedStep;
              if (
                lastCompletedStep >= 0 &&
                lastCompletedStep < steps.length - 1
              ) {
                setCurrentStep(lastCompletedStep + 1);
              }

              // Store business group info if available
              if (data.businessGroup) {
                setKybDetails({
                  businessGroup: data.businessGroup,
                  businessGroupName: data.businessGroupName || "",
                });
              }
            }
          }

          setIsLoading(false);
        } catch (error) {
          console.error("Error checking business status:", error);
          setIsLoading(false);
        }
      }
    };

    checkBusinessStatus();
  }, [user?.email?.address, navigate, steps.length]);

  const handleInputChange = async (field: string, value: string | string[]) => {
    let processedValue: string | string[] | null = value;
    if ((field === "hqCountry" || field === "hqState") && value === "") {
      processedValue = null;
    }
    const updatedInfo = {
      ...businessInfo,
      [field]: processedValue,
    };
    setBusinessInfo(updatedInfo);

    try {
      const response = await API.post("/business-lead", {
        ...updatedInfo,
        email: user.email.address,
        id: leadId,
        isComplete: false,
      });

      if (!leadId && response.data?.id) {
        setLeadId(response.data.id);
      }
    } catch (error) {
      console.error("Error saving progress:", error);
    }
  };

  const handleMultiSelect = (field: string, value: string) => {
    const updatedField = businessInfo[field].includes(value)
      ? businessInfo[field].filter((v) => v !== value)
      : [...businessInfo[field], value];

    const updatedInfo = {
      ...businessInfo,
      [field]: updatedField,
    };

    setBusinessInfo(updatedInfo);

    // Save to backend after updating the multi-select field
    try {
      API.post("/business-lead", {
        ...updatedInfo,
        email: user.email.address,
        id: leadId,
        isComplete: false,
      });
    } catch (error) {
      console.error("Error saving progress:", error);
    }
  };

  const validateStep = (stepFields: string[]) => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    stepFields.forEach((field) => {
      const error = validationRules[field](businessInfo[field], businessInfo);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleNext = () => {
    const currentStepFields = steps[currentStep].fields;
    if (validateStep(currentStepFields)) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (validateStep(steps[currentStep].fields)) {
      try {
        const personaTemplateId = getPersonaTemplateId(businessInfo);
        const businessGroup = getBusinessGroup(businessInfo);
        const businessGroupName = getBusinessGroupName(businessInfo);

        // Store the group information
        setKybDetails({
          businessGroup,
          businessGroupName,
        });

        // Submit business information with classification data
        await API.post("/business-lead", {
          ...businessInfo,
          email: user.email.address,
          id: leadId,
          isComplete: true,
          personaTemplateId,
          businessGroup,
          businessGroupName,
        });

        // If in Group 4 (Rejected), navigate to a rejection page
        if (businessGroup === 4) {
          navigate("/accounts/rejected");
          return;
        }

        // If in Group 3 (Hand-off), show the modal
        if (businessGroup === 3) {
          setShowHandoffModal(true);
          return;
        }

        navigate("/accounts");
      } catch (error) {
        console.error("Error submitting business information:", error);
      }
    }
  };

  const renderField = (field: string) => {
    const error = errors[field];
    const baseInputClasses =
      "w-full px-4 py-3 border rounded-lg focus:outline-none text-gray-800";
    const inputClasses = `${baseInputClasses} ${
      error ? "border-red-500" : "border-gray-200 focus:border-[#6333A4]"
    }`;

    switch (field) {
      case "name":
        return (
          <div key={field} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name
            </label>
            <input
              type="text"
              value={businessInfo[field]}
              onChange={(e) => handleInputChange(field, e.target.value)}
              className={inputClasses}
              placeholder="Enter your full name"
            />
            {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
          </div>
        );
      case "companyName":
        return (
          <div key={field} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Name
            </label>
            <input
              type="text"
              value={businessInfo[field]}
              onChange={(e) => handleInputChange(field, e.target.value)}
              className={inputClasses}
              placeholder="Enter company name"
            />
            {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
          </div>
        );

      case "industry":
        return (
          <div key={field} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Industry
            </label>
            <select
              value={businessInfo.industry}
              onChange={(e) => handleInputChange("industry", e.target.value)}
              className={inputClasses}
            >
              <option value="">Select industry</option>
              {INDUSTRY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
          </div>
        );

      case "hqCountry":
        return (
          <div key={field} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Headquarters Country
            </label>
            <select
              value={businessInfo.hqCountry || ""}
              onChange={(e) => handleInputChange("hqCountry", e.target.value)}
              className={inputClasses}
            >
              <option value="">Select country</option>
              {countryOptions.map((country) => (
                <option key={country.value} value={country.value}>
                  {country.label}
                </option>
              ))}
            </select>
            {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
          </div>
        );

      case "hqState":
        return (
          <div key={field} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Headquarters State/Province
            </label>
            <select
              value={businessInfo.hqState || ""}
              onChange={(e) => handleInputChange("hqState", e.target.value)}
              className={inputClasses}
              disabled={businessInfo.hqCountry !== CountryCodeAlpha2.US}
            >
              <option value="">Select state</option>
              {usStateOptions.map((state) => (
                <option key={state.value} value={state.value}>
                  {state.label}
                </option>
              ))}
            </select>
            {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
            {businessInfo.hqCountry !== CountryCodeAlpha2.US && (
              <p className="mt-1 text-sm text-gray-500">
                Only required for US-based businesses
              </p>
            )}
          </div>
        );

      case "monthlyVolume":
        return (
          <div key={field} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Monthly Payment Volume
            </label>
            <select
              value={businessInfo.monthlyVolume}
              onChange={(e) =>
                handleInputChange("monthlyVolume", e.target.value)
              }
              className={inputClasses}
            >
              <option value="">Select monthly volume</option>
              {MONTHLY_VOLUME_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
          </div>
        );

      case "transactionsPerMonth":
        return (
          <div key={field} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of Transactions per Month
            </label>
            <select
              value={businessInfo.transactionsPerMonth}
              onChange={(e) =>
                handleInputChange("transactionsPerMonth", e.target.value)
              }
              className={inputClasses}
            >
              <option value="">Select transaction volume</option>
              {TRANSACTIONS_PER_MONTH_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
          </div>
        );

      case "paymentRails":
      case "fiatCurrencies":
      case "digitalCurrencies":
        const options = {
          paymentRails: PAYMENT_RAILS_OPTIONS,
          fiatCurrencies: FIAT_CURRENCIES_OPTIONS,
          digitalCurrencies: DIGITAL_CURRENCIES_OPTIONS,
        }[field];

        return (
          <div key={field} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {field.charAt(0).toUpperCase() +
                field.slice(1).replace(/([A-Z])/g, " $1")}
            </label>
            <div className="space-y-2">
              {options.map((option) => (
                <label key={option} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={businessInfo[field].includes(option)}
                    onChange={() => handleMultiSelect(field, option)}
                    className="rounded border-gray-300 text-[#6333A4] focus:ring-[#6333A4]"
                  />
                  <span className="text-sm text-gray-700">{option}</span>
                </label>
              ))}
            </div>
            {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
          </div>
        );

      case "useCase":
        return (
          <div key={field} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What are you looking to solve?
            </label>
            <div className="space-y-2">
              {USE_CASE_OPTIONS.map((option) => (
                <label key={option} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={businessInfo.useCase.includes(option)}
                    onChange={() => handleMultiSelect("useCase", option)}
                    className="rounded border-gray-300 text-[#6333A4] focus:ring-[#6333A4]"
                  />
                  <span className="text-sm text-gray-700">{option}</span>
                </label>
              ))}
            </div>
            {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
          </div>
        );

      case "platformPreference":
        return (
          <div key={field} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Platform Preference
            </label>
            <div className="space-y-2">
              {PLATFORM_PREFERENCE_OPTIONS.map((option) => (
                <label key={option} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={businessInfo.platformPreference.includes(option)}
                    onChange={() =>
                      handleMultiSelect("platformPreference", option)
                    }
                    className="rounded border-gray-300 text-[#6333A4] focus:ring-[#6333A4]"
                  />
                  <span className="text-sm text-gray-700">{option}</span>
                </label>
              ))}
            </div>
            {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
          </div>
        );

      case "customerTypes":
        return (
          <div key={field} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Who are your customers?
            </label>
            <p className="text-sm text-gray-500 mb-4">Select all that apply:</p>
            <div className="space-y-2">
              {CUSTOMER_TYPE_OPTIONS.map((option) => (
                <label key={option} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={businessInfo.customerTypes.includes(option)}
                    onChange={() => handleMultiSelect("customerTypes", option)}
                    className="rounded border-gray-300 text-[#6333A4] focus:ring-[#6333A4]"
                  />
                  <span className="text-sm text-gray-700">{option}</span>
                </label>
              ))}
            </div>
            {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
          </div>
        );

      default:
        return null;
    }
  };

  const handleProceedToKYB = () => {
    setShowHandoffModal(false);
    navigate("/accounts");
  };

  const handleScheduleCall = () => {
    window.open("https://calendly.com/john-getbeam", "_blank");
  };

  // Handoff Modal
  const HandoffModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full">
        <h2 className="text-2xl font-medium mb-4">
          Schedule a Meeting with John
        </h2>
        <p className="text-gray-600 mb-6">
          Based on your business needs, we recommend speaking with our
          specialist, John, before proceeding with the KYB/KYC process.
        </p>
        <p className="text-gray-600 mb-6">
          This allows us to better understand your requirements and ensure a
          smooth onboarding experience.
        </p>
        <div className="flex flex-col space-y-4">
          <button
            onClick={handleScheduleCall}
            className="px-6 py-3 bg-[#6333A4] text-white rounded-lg hover:bg-[#522c86]"
          >
            Schedule with John
          </button>
          <button
            onClick={handleProceedToKYB}
            className="px-6 py-3 border border-[#6333A4] text-[#6333A4] rounded-lg hover:bg-gray-50"
          >
            Continue to KYB/KYC
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col items-center pt-24 px-6 relative">
      {showHandoffModal && <HandoffModal />}
      <div className="w-full max-w-2xl">
        <div className="mb-8">
          <h2 className="text-2xl font-medium mb-2">
            {steps[currentStep].title}
          </h2>
          <div className="flex gap-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-1 flex-1 rounded ${
                  index <= currentStep ? "bg-[#6333A4]" : "bg-gray-200"
                }`}
              />
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-8">
          {steps[currentStep].fields.map((field) => renderField(field))}

          <div className="flex justify-between mt-8">
            <button
              onClick={handleBack}
              disabled={currentStep === 0}
              className={`px-6 py-2 rounded-lg ${
                currentStep === 0
                  ? "bg-gray-100 text-gray-400"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Back
            </button>
            <button
              onClick={
                currentStep === steps.length - 1 ? handleSubmit : handleNext
              }
              className="px-6 py-2 bg-[#6333A4] text-white rounded-lg hover:bg-[#522c86]"
            >
              {currentStep === steps.length - 1 ? "Submit" : "Next"}
            </button>
          </div>
        </div>
      </div>
      {isLoading && <div className="loading-spinner">Loading...</div>}

      {/* Group 3 (Hand-off) user notification - displayed when business group is already known */}
      {kybDetails.businessGroup === 3 && !showHandoffModal && (
        <div className="w-full max-w-2xl mt-8 bg-blue-50 p-6 rounded-lg">
          <h3 className="text-lg font-medium mb-2 text-blue-800">
            High Volume Business
          </h3>
          <p className="text-blue-700 mb-4">
            Based on your business profile, we recommend scheduling a call with
            our specialist, Diana, to ensure a smooth onboarding experience.
          </p>
          <div className="flex space-x-4">
            <button
              onClick={handleScheduleCall}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Schedule with Diana
            </button>
            <button
              onClick={handleProceedToKYB}
              className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
            >
              Continue to KYB/KYC
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
